'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { changeLanguage } from '@eliadmin/core/src/server/api-client';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getFlagEmoji } from '@eliadmin/core/src/utils/locale-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { Button } from '../ui/button';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { Paragraph } from '../ui/paragraph';

export function LanguageSelect() {
  const isDesktop = useBreakpoint('sm');
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, setIsOpen } = useDisclosure();

  const currentLocale = useLocaleStore(s => s.currentLocale);
  const supportedLocales = useLocaleStore(s => s.supportedLocales);

  const pathname = usePathname();

  const selectedLocale = useMemo(
    () => supportedLocales.find(l => l.locale === currentLocale),
    [supportedLocales, currentLocale],
  );

  const buildNewRoute = (newLocale: string) => {
    // If your app always prefixes locale in the path, replace it; otherwise prefix it.
    if (pathname.startsWith(`/${currentLocale}`)) {
      return pathname.replace(`/${currentLocale}`, `/${newLocale}`);
    }
    return `/${newLocale}${pathname}`;
  };

  const handleLocaleChange = async (newLocale: string) => {
    if (!newLocale || newLocale === currentLocale) return;

    const newRoute = buildNewRoute(newLocale);

    setIsLoading(true);
    try {
      await changeLanguage({ locale: newLocale });
      window.location.replace(newRoute);
    } finally {
      // In practice, replace() navigates away; this is here for safety/tests.
      setIsLoading(false);
    }
  };

  // Optional: if supportedLocales can change during runtime, keep loading false.
  useEffect(() => {
    setIsLoading(false);
  }, [currentLocale, supportedLocales]);

  return (
    <div className={cn('w-full sm:w-auto', 'ea-language-select')}>
      <DropdownMenu modal={false} onOpenChange={setIsOpen} open={isOpen}>
        <DropdownMenuTrigger
          asChild
          className={cn(
            'ea-language-select__trigger',
            'w-full p-0 sm:w-fit',
            isOpen && 'bg-border',
          )}
          disabled={isLoading}
        >
          <Button variant="outline" size={isDesktop ? 'icon' : 'none'} className="h-12 sm:h-auto">
            {isLoading ? (
              <Spinner className={cn('w-8', 'ea-language-select__spinner')} />
            ) : selectedLocale ? (
              <div className="flex items-center justify-start gap-2 sm:justify-center">
                {selectedLocale.image?.url ? (
                  <>
                    <Image
                      src={selectedLocale.image.url}
                      alt={resolveImageAlt(selectedLocale?.image, selectedLocale?.locale)}
                      className={cn(
                        'max-h-6 min-h-6 min-w-6 max-w-6 object-contain object-center',
                        'ea-language-select__flag',
                      )}
                      width={20}
                      height={20}
                    />
                    {!isDesktop && (
                      <Paragraph className="font-normal capitalize" color="black">
                        {selectedLocale.name}
                      </Paragraph>
                    )}
                  </>
                ) : (
                  <span className={cn('text-xl', 'ea-language-select__flag')}>
                    {getFlagEmoji(selectedLocale.locale)}
                  </span>
                )}
              </div>
            ) : (
              <span className={cn('px-2 text-xl', 'ea-language-select__flag')}>
                {getFlagEmoji(currentLocale)}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={10}
          className={cn(
            'ea-language-select__content',
            'flex w-full flex-col items-start justify-start overflow-hidden rounded-none border-none bg-white p-0 sm:w-fit',
          )}
        >
          {supportedLocales.map(l => {
            const isActive = l.locale === currentLocale;

            return (
              <DropdownMenuItem
                key={`${l.name}-${l.locale}`}
                onSelect={e => {
                  e.preventDefault();
                  void handleLocaleChange(l.locale);
                }}
                disabled={isLoading || isActive}
                className={cn(
                  'ea-language-select__item',
                  'w-full cursor-pointer rounded-none px-4 focus:bg-neutral-100',
                )}
              >
                <div className="flex items-center justify-start gap-2">
                  <Image
                    src={l?.image?.url ?? ''}
                    alt={resolveImageAlt(l?.image, l?.locale)}
                    className={cn(
                      'max-h-6 min-h-6 min-w-6 max-w-6 object-contain',
                      'ea-language-select__flag',
                    )}
                    width={0}
                    height={0}
                  />
                  <span className="whitespace-nowrap text-base font-normal text-black">
                    {l.name}
                  </span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
